import { useSearchParams } from 'react-router-dom';
import { useMemo, useCallback, useState } from 'react';
import { isEmpty, isNullOrUndefined } from '@common/utils';
import { AnyObject, QueryManagerOptions } from '@common/types/queryState';

export type UrlSchema<T> = {
    [K in keyof T]: (value: string | null) => T[K];
};

type UrlOptions = Pick<QueryManagerOptions, 'skipUrlSync' | 'prefix'>;

const isDeletable = (value: unknown): boolean => {
    return (
        isNullOrUndefined(value) ||
        (typeof value !== 'number' && isEmpty(value))
    );
};

const serializeValue = (value: unknown): string => {
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

export const useUrlState = <T extends AnyObject>(
    schema: UrlSchema<T>,
    initialState?: Partial<T>,
    options: UrlOptions = {},
) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [localParams, setLocalParams] = useState(new URLSearchParams());

    const { skipUrlSync, prefix } = options;

    const activeParams = skipUrlSync ? localParams : searchParams;
    const updateParams = useCallback(
        (updater: (prev: URLSearchParams) => URLSearchParams) => {
            if (skipUrlSync) {
                setLocalParams(updater);
            } else {
                setSearchParams(updater, { replace: true });
            }
        },
        [skipUrlSync, setSearchParams],
    );

    const getUrlKey = useCallback(
        (key: string) => (prefix ? `${prefix}_${key}` : key),
        [prefix],
    );

    const state = useMemo(() => {
        return (Object.keys(schema) as Array<keyof T>).reduce((acc, key) => {
            const urlKey = getUrlKey(key as string);
            const urlValue = activeParams.get(urlKey);

            const componentDefault = initialState?.[key];
            const schemaFallback = schema[key](null);

            acc[key] =
                urlValue !== null
                    ? schema[key](urlValue)
                    : (componentDefault ?? schemaFallback);

            return acc;
        }, {} as T);
    }, [schema, getUrlKey, activeParams, initialState]);

    const setState = useCallback(
        (update: Partial<T> | ((prev: T) => Partial<T>)) => {
            updateParams((prevParams) => {
                const nextState =
                    typeof update === 'function' ? update(state) : update;
                const params = new URLSearchParams(prevParams);

                Object.entries(nextState).forEach(([key, value]) => {
                    const urlKey = getUrlKey(key);

                    if (isDeletable(value)) params.delete(urlKey);
                    else params.set(urlKey, serializeValue(value));
                });

                return params;
            });
        },
        [updateParams, state, getUrlKey],
    );

    const reset = useCallback(
        ({ except = [] }: { except?: Array<keyof T> } = {}) => {
            updateParams((prevParams) => {
                const params = new URLSearchParams(prevParams);

                (Object.keys(schema) as Array<keyof T>).forEach((key) => {
                    if (!except.includes(key)) {
                        params.delete(getUrlKey(key as string));
                    }
                });

                return params;
            });
        },
        [schema, getUrlKey, updateParams],
    );

    return { state, setState, reset };
};
